import InputLabel from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import { Moment } from 'moment';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { Calendar, Edit3, Trash2 } from 'react-feather';
import { useSelector } from 'react-redux';
import { examsSelectors } from '../../../slices/exams/examsSlice';
import { RootState } from '../../../store/store';
import { useUserExamUpdate } from '../../../utils/updateUserExam';
import {
  createSchoolExam,
  deleteCustomExam,
  fetchExams,
} from '../../../services/exams';
// import { lecturesSelectors } from '../../../slices/lectures/lecturesSlice';
import CustomExamDatePicker from '../../Global/CustomExamDatePicker';
import CustomModal from '../../Global/CustomModal';
import { hapticsImpactLight } from '../../../utils/haptics';
import { enqueueSnackbar } from 'notistack';
import {
  isCustomSchoolExam,
  isVerifiedSchoolExam,
} from '../../../utils/nbmeExamUtils';

interface SchoolExamInfoProps {
  isCreateMode?: boolean;
  newExamCreated?: boolean;
  setExamCreated?: (examCreated: boolean) => void;
  setShouldRefreshStudySession?: (shouldRefreshStudySession: boolean) => void;
  isOnboarding?: boolean;
  closeSecondaryScreen?: () => void;
}

const SchoolExamInfo: React.FC<SchoolExamInfoProps> = ({
  isCreateMode,
  newExamCreated = false,
  setExamCreated,
  setShouldRefreshStudySession,
  isOnboarding = false,
  closeSecondaryScreen,
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const primaryExam = useSelector(examsSelectors.selectPrimaryExam);
  const userSchool = useSelector((state: RootState) => state.school.school);
  const existingExams = useSelector(examsSelectors.selectAll);
  // const lectures = useSelector(lecturesSelectors.selectAll);

  const [examName, setExamName] = useState<string>('');
  const [examDate, setExamDate] = useState<Moment | null>(null);
  const [showExamDateModal, setShowExamDateModal] = useState(false);

  const [isChangingExamName, setIsChangingExamName] = useState(false);
  const [isDeletingExam, setIsDeletingExam] = useState(false);
  const [deleteConfirmationName, setDeleteConfirmationName] =
    useState<string>('');

  const { updateExam } = useUserExamUpdate(undefined, {
    showSuccessMessage: false,
  });

  const ambassadorMode = currentUser?.ambassador && isOnboarding;
  const routineChecks = currentUser && userSchool && !ambassadorMode;

  const isEditingExam = Boolean(
    newExamCreated ||
      (!isCreateMode && primaryExam?.id && primaryExam?.type === 'CUSTOM')
  );

  // If the exam is verified, only admins and ambassadors can edit it
  const readOnlyMode = Boolean(
    isEditingExam &&
      isVerifiedSchoolExam(primaryExam) &&
      currentUser?.role !== 'admin' &&
      !currentUser?.ambassador
  );

  // If we are in edit mode, set the exam name and date to the primary exam's name and date
  useEffect(() => {
    if (isEditingExam && primaryExam) {
      setExamName(primaryExam.name);
      setExamDate(primaryExam.examDate ? moment(primaryExam.examDate) : null);
    }
  }, [isEditingExam]);

  // If we are in edit mode, update the exam name in the backend every time it changes
  useEffect(() => {
    const updateExamNameEffect = async () => {
      if (
        isEditingExam &&
        (isCustomSchoolExam(primaryExam) ||
          (isVerifiedSchoolExam(primaryExam) && !readOnlyMode)) &&
        examName &&
        primaryExam &&
        routineChecks
      ) {
        if (examName !== primaryExam.name) {
          await updateExam({
            userId: currentUser.id,
            examId: primaryExam.id,
            examName: examName,
          });
        }
      }
    };
    updateExamNameEffect();
  }, [isEditingExam, examName]);

  // If we are in edit mode, update the exam date in the backend every time it changes
  useEffect(() => {
    const updateExamDateEffect = async () => {
      if (
        isEditingExam &&
        (isCustomSchoolExam(primaryExam) ||
          (isVerifiedSchoolExam(primaryExam) && !readOnlyMode)) &&
        examDate &&
        examDate.isValid() &&
        primaryExam &&
        routineChecks
      ) {
        if (!examDate.isSame(primaryExam.examDate, 'day')) {
          const transformedDate = moment.utc(examDate).startOf('day').hour(12);

          if (transformedDate.isValid()) {
            const isoDateString = transformedDate.toISOString();
            await updateExam({
              userId: currentUser.id,
              examId: primaryExam.id,
              examDate: isoDateString,
            });
            setShouldRefreshStudySession?.(true);
          } else {
            console.error(
              '#### Error: Transformed date is invalid. Original date:',
              examDate.toString()
            );
          }
        }
      } else if (examDate && !examDate.isValid()) {
        console.error('#### Error: examDate from state is invalid:', examDate);
      }
    };
    updateExamDateEffect();
  }, [
    isEditingExam,
    examDate,
    primaryExam,
    routineChecks,
    currentUser,
    updateExam,
    setShouldRefreshStudySession,
  ]);

  // Name and date fields are valid
  const fieldsValid = Boolean(
    examName.trim() && (!examDate || moment.isMoment(examDate))
  );

  // If we are in create mode, create a new exam as soon as the name and date fields are valid
  useEffect(() => {
    const createNewExam = async () => {
      if (!isCreateMode || newExamCreated) return;
      if (readOnlyMode) return;
      if (!fieldsValid) return;
      if (!routineChecks) return;
      if (existingExams.find((exam) => exam.name === examName)) return;

      const newExam = await createSchoolExam(
        examName,
        examDate ? moment.utc(examDate) : null,
        userSchool?.id
      );
      if (newExam) setExamCreated?.(true);
      fetchExams();
      setShouldRefreshStudySession?.(true);
    };

    createNewExam();
  }, [examName, examDate, fieldsValid, routineChecks]);

  const handleDeleteExam = async () => {
    if (readOnlyMode || isCreateMode || isOnboarding) return;
    if (primaryExam) {
      const response = await deleteCustomExam(primaryExam.id);
      if (response.success) {
        await fetchExams();
        closeSecondaryScreen?.();
      } else {
        const errorMessage = response.error || 'Failed to delete exam';
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
        });
      }
      setDeleteConfirmationName('');
      setIsDeletingExam(false);
    }
  };

  const renderExamName = (isChangingExamName: boolean) => {
    if (!isCreateMode) {
      if (isChangingExamName && !readOnlyMode) {
        return (
          <TextField
            value={examName}
            onChange={(e) => setExamName(e.target.value)}
            onBlur={() => setIsChangingExamName(false)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setIsChangingExamName(false);
              }
            }}
          />
        );
      } else {
        return (
          <div className="exam-title">
            <span>Modify</span>
            <h1>
              <span>{examName}</span>
              {!readOnlyMode && (
                <>
                  <button
                    className="button button--icon-only"
                    onClick={() => setIsChangingExamName(true)}
                  >
                    <Edit3 />
                  </button>
                  <button
                    className="button button--icon-only"
                    onClick={() => setIsDeletingExam(true)}
                  >
                    <Trash2 />
                  </button>
                </>
              )}
            </h1>
          </div>
        );
      }
    } else {
      return (
        <div className="exam-name-input">
          <InputLabel className="m-b-0-50">Exam Name</InputLabel>
          <TextField
            value={examName}
            onChange={(e) => setExamName(e.target.value)}
            placeholder="Ex. Physio, MSK, Immuno..."
          />
        </div>
      );
    }
  };

  const renderExamDate = () => {
    if (readOnlyMode) {
      return (
        <div className="exam-date-picker">
          <span style={{ color: 'var(--type-body-inverse)' }}>
            {moment(examDate).format('MMMM D, YYYY')}
          </span>
        </div>
      );
    }
    if (isCreateMode) {
      return (
        <div className="exam-date-picker">
          <CustomExamDatePicker
            value={examDate}
            onChange={(newValue: Moment | null) => {
              if (!newValue) {
                setExamDate(null);
                return;
              }

              if (!newValue.isValid()) {
                console.error('#### onChange received invalid date:', newValue);
                return;
              }

              setExamDate(newValue);
            }}
            disabled={readOnlyMode}
            skipDateSelection={() => {
              setExamDate(null);
            }}
          />
        </div>
      );
    } else {
      return (
        <>
          <button
            className="button button--glass"
            onClick={() => {
              setShowExamDateModal(true);
              hapticsImpactLight();
            }}
          >
            <Calendar />
            {examDate ? moment(examDate).format('MMMM D, YYYY') : 'Select Date'}
          </button>
          {showExamDateModal && (
            <CustomModal
              name="modify-exam-date"
              open={showExamDateModal}
              onClose={() => setShowExamDateModal(false)}
              glass={true}
            >
              <div className="modal_content">
                <CustomExamDatePicker
                  value={examDate}
                  onChange={(newValue: Moment | null) => {
                    if (!newValue) {
                      setExamDate(null);
                      return;
                    }

                    if (!newValue.isValid()) {
                      console.error(
                        '#### onChange received invalid date:',
                        newValue
                      );
                      return;
                    }

                    setExamDate(newValue);
                  }}
                  disabled={readOnlyMode}
                />
              </div>
            </CustomModal>
          )}
        </>
      );
    }
  };

  return (
    <div className="school-exam-info">
      {renderExamName(isChangingExamName)}
      <InputLabel id="exam-date-selection" className="m-t-1-50 m-b-0-50">
        Exam Date
      </InputLabel>
      {renderExamDate()}
      <CustomModal
        open={isDeletingExam}
        onClose={() => {
          setIsDeletingExam(false);
          setDeleteConfirmationName('');
        }}
        name="delete-exam"
        glass={true}
        blurs={true}
        blurType="error"
      >
        <div className="modal_header">
          <h2>Delete Exam</h2>
        </div>
        <div className="modal_content">
          <p>To confirm deletion, please type the exam name below.</p>
          <TextField
            autoFocus
            margin="dense"
            label="Exam Name"
            type="text"
            fullWidth
            variant="outlined"
            value={deleteConfirmationName}
            onChange={(e) => setDeleteConfirmationName(e.target.value)}
            sx={{ marginBlockStart: '1rem', marginBlockEnd: '0.5rem' }}
          />
          <p>This action cannot be undone.</p>
        </div>
        <div className="modal_actions">
          <button
            className="button button--glass"
            onClick={() => {
              hapticsImpactLight();
              setIsDeletingExam(false);
              setDeleteConfirmationName('');
            }}
          >
            Cancel
          </button>
          <button
            className="button button--error"
            onClick={() => {
              hapticsImpactLight();
              handleDeleteExam();
            }}
            disabled={deleteConfirmationName !== examName}
          >
            Delete
          </button>
        </div>
      </CustomModal>
    </div>
  );
};

export default SchoolExamInfo;
